import React, { useState, useRef, useEffect } from 'react';
import { speakText, setReplay, setHud, sfx } from './lzAudio';

// Three more Brain Boost games (Memory Match, Big & Small, What's Missing?).
// Same content as the app's LearningZone/MemoryMatchRN, BigOrSmallRN and
// WhatsMissingRN. Static for now - GamelevelItems appends them to the Brain
// Boost category until GAME_LIST returns them (see LearningZoneGames.js).
// Each reports onFinish(correct, total) like OddOneOut.

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const DIFF_COLOR = { Easy: '#22C55E', Medium: '#F59E0B', Hard: '#F97316', Expert: '#8B5CF6' };
const starsFor = (pct) => (pct >= 100 ? 3 : pct >= 70 ? 2 : pct >= 40 ? 1 : 0);

const Dots = ({ levels, results, index, finished }) => (
    <div className="bbx-dots">
        {levels.map((l, i) => {
            const r = results[i];
            const now = i === index && !finished;
            const bg = r === true ? '#22C55E' : r === false ? '#FDE68A' : now ? DIFF_COLOR[l.difficulty] : '#E2E8F0';
            return <span key={l.id} className={`bbx-dot ${now ? 'now' : ''}`} style={{ background: bg }}>{r === true ? '✓' : r === false ? '★' : l.id}</span>;
        })}
    </div>
);

// One compact row - "Level x of n", the progress dots (children) and the
// difficulty chip - so the question and choices get the board's height.
const LevelBar = ({ text, difficulty, children }) => (
    <div className="bbx-levelbar">
        <span className="bbx-leveltext">{text}</span>
        {children}
        <span className="bbx-chip" style={{ background: DIFF_COLOR[difficulty] }}>{difficulty}</span>
    </div>
);

const Final = ({ correct, total, title, onReplay, children }) => {
    const pct = Math.round((correct / total) * 100);
    const stars = starsFor(pct);
    return (
        <div className="bbx-final">
            <div style={{ fontSize: 64 }}>{stars === 3 ? '🏆' : stars >= 1 ? '🎉' : '💪'}</div>
            <h3>{stars === 3 ? title : stars >= 1 ? 'Great job!' : 'Good try! Play again!'}</h3>
            <div className="bbx-stars">{[1, 2, 3].map((s) => <span key={s} style={{ opacity: s <= stars ? 1 : 0.2 }}>⭐</span>)}</div>
            <p>You got {correct} of {total} on the first try!</p>
            {children}
            <button type="button" className="bbx-btn" onClick={onReplay}>🔄 Play again</button>
        </div>
    );
};

// ─── Memory Match ───────────────────────────────────────────────────────────

// Preschool level: 2, 3 then 4 pairs of familiar pictures.
const MEMORY_LEVELS = [
    { id: 1, pairs: 2, cols: 2, difficulty: 'Easy', pool: ['🐶', '🐱', '🐰', '🐻'] },
    { id: 2, pairs: 3, cols: 3, difficulty: 'Easy', pool: ['🍎', '🍌', '🍓', '🍉'] },
    { id: 3, pairs: 4, cols: 4, difficulty: 'Medium', pool: ['🚗', '🚂', '🚌', '⛵', '🚲'] },
];
const MEMORY_INTRO = 'Find the matching pairs!';

const buildDeck = (level) => {
    const faces = shuffle(level.pool).slice(0, level.pairs);
    return shuffle([...faces, ...faces].map((emoji, i) => ({ key: `${level.id}-${i}`, emoji })));
};

export const MemoryMatch = ({ onFinish }) => {
    const [index, setIndex] = useState(0);
    const [deck, setDeck] = useState(() => buildDeck(MEMORY_LEVELS[0]));
    const [open, setOpen] = useState([]);
    const [matched, setMatched] = useState([]);
    const [moves, setMoves] = useState(0);
    const [busy, setBusy] = useState(false);
    const [finished, setFinished] = useState(false);
    // A level counts as "correct" when solved in at most pairs + 3 moves.
    const [results, setResults] = useState([]);
    const timer = useRef(null);

    const level = MEMORY_LEVELS[index];
    const levelDone = matched.length === deck.length;

    useEffect(() => () => clearTimeout(timer.current), []);
    useEffect(() => {
        const t = setTimeout(() => speakText(MEMORY_INTRO), 400);
        return () => clearTimeout(t);
    }, [index]);
    useEffect(() => {
        setReplay(finished ? 'You have a super memory!' : levelDone ? 'Level done! Tap next.' : MEMORY_INTRO);
        return () => setReplay(null);
    }, [finished, levelDone]);
    useEffect(() => {
        setHud({ score: results.filter(Boolean).length, total: MEMORY_LEVELS.length, question: `🃏 ${MEMORY_INTRO}` });
        return () => setHud(null);
    }, [results]);

    const flip = (card) => {
        if (busy || open.includes(card.key) || matched.includes(card.key)) return;
        sfx.tap();
        const nextOpen = [...open, card.key];
        setOpen(nextOpen);
        if (nextOpen.length < 2) return;
        const usedMoves = moves + 1;
        setMoves(usedMoves);
        const [a, b] = nextOpen.map((k) => deck.find((c) => c.key === k));
        if (a.emoji === b.emoji) {
            const nextMatched = [...matched, a.key, b.key];
            setMatched(nextMatched);
            setOpen([]);
            sfx.correct();
            if (nextMatched.length === deck.length) {
                setResults((r) => [...r, usedMoves <= level.pairs + 3]);
                speakText('Hooray! You found them all!');
            }
        } else {
            setBusy(true);
            sfx.wrong();
            timer.current = setTimeout(() => { setOpen([]); setBusy(false); }, 1300);
        }
    };

    const startLevel = (i) => {
        setIndex(i);
        setDeck(buildDeck(MEMORY_LEVELS[i]));
        setOpen([]);
        setMatched([]);
        setMoves(0);
    };

    const next = () => {
        if (index + 1 < MEMORY_LEVELS.length) {
            startLevel(index + 1);
            return;
        }
        setFinished(true);
        sfx.finish();
        speakText('Amazing! You have a super memory!');
        // Finishing all levels is the goal; fewer moves only adds stars.
        onFinish?.(Math.max(1, results.filter(Boolean).length), MEMORY_LEVELS.length);
    };

    const replay = () => {
        setResults([]);
        setFinished(false);
        startLevel(0);
    };

    if (finished) {
        return (
            <div className="bbx">
                <Final correct={Math.max(1, results.filter(Boolean).length)} total={MEMORY_LEVELS.length} title="Super memory!" onReplay={replay} />
                <style>{styles}</style>
            </div>
        );
    }

    return (
        <div className="bbx">
            <LevelBar text={`Level ${level.id} of ${MEMORY_LEVELS.length} · Moves: ${moves}`} difficulty={level.difficulty} />
            <div key={index} className="bbx-memory" style={{ gridTemplateColumns: `repeat(${level.cols}, minmax(0, 160px))` }}>
                {deck.map((card) => {
                    const isOpen = open.includes(card.key) || matched.includes(card.key);
                    return (
                        <button
                            type="button"
                            key={card.key}
                            className={`bbx-mcard ${isOpen ? 'open' : ''} ${matched.includes(card.key) ? 'matched' : ''}`}
                            onClick={() => flip(card)}
                            disabled={isOpen}
                            aria-label={isOpen ? card.emoji : 'Hidden card'}
                        >
                            {isOpen ? card.emoji : '❓'}
                        </button>
                    );
                })}
            </div>
            <div className="bbx-bottom">
                {levelDone ? (
                    <>
                        <div className="bbx-done">🎉 All pairs found in {moves} moves!</div>
                        <button type="button" className="bbx-btn" onClick={next}>{index + 1 < MEMORY_LEVELS.length ? 'Next level ➜' : 'Finish 🏁'}</button>
                    </>
                ) : (
                    <div className="bbx-tip">Pairs found: {matched.length / 2} / {level.pairs}</div>
                )}
            </div>
            <style>{styles}</style>
        </div>
    );
};

// ─── Big & Small ────────────────────────────────────────────────────────────

// Preschool level: just "big" or "small", with sizes far apart. Two
// pictures to start, three at the end.
const SIZE_LEVELS = [
    { id: 1, difficulty: 'Easy', emoji: '🐘', ask: 'big', sizes: [40, 110] },
    { id: 2, difficulty: 'Easy', emoji: '🍎', ask: 'small', sizes: [40, 110] },
    { id: 3, difficulty: 'Easy', emoji: '⚽', ask: 'big', sizes: [40, 110] },
    { id: 4, difficulty: 'Easy', emoji: '🐶', ask: 'small', sizes: [40, 110] },
    { id: 5, difficulty: 'Medium', emoji: '🌳', ask: 'big', sizes: [36, 70, 110] },
    { id: 6, difficulty: 'Medium', emoji: '🐢', ask: 'small', sizes: [36, 70, 110] },
];
const sizeQuestion = (l) => `Tap the ${l.ask} one!`;
const sizeTarget = (l) => (l.ask === 'big' ? Math.max(...l.sizes) : Math.min(...l.sizes));

export const BigOrSmall = ({ onFinish }) => {
    const [index, setIndex] = useState(0);
    const [items, setItems] = useState(() => shuffle(SIZE_LEVELS[0].sizes));
    const [results, setResults] = useState([]);
    const [wrong, setWrong] = useState([]);
    const [solved, setSolved] = useState(false);
    const [finished, setFinished] = useState(false);

    const level = SIZE_LEVELS[index];
    const target = sizeTarget(level);

    useEffect(() => {
        const t = setTimeout(() => speakText(sizeQuestion(level)), 400);
        return () => clearTimeout(t);
    }, [level]);
    useEffect(() => {
        setReplay(finished ? 'Great job! Tap play again.' : sizeQuestion(level));
        return () => setReplay(null);
    }, [finished, level]);
    useEffect(() => {
        setHud({ score: results.filter(Boolean).length, total: SIZE_LEVELS.length, question: `${level.ask === 'big' ? '🐘' : '🐭'} ${sizeQuestion(level)}` });
        return () => setHud(null);
    }, [results, level]);

    const choose = (size) => {
        if (solved || wrong.includes(size)) return;
        if (size === target) {
            setSolved(true);
            setResults((r) => [...r, wrong.length === 0]);
            sfx.correct();
            speakText(`Yes! That is the ${level.ask} one!`);
        } else {
            setWrong((w) => [...w, size]);
            sfx.wrong();
            speakText(`Oops! Look for the ${level.ask} one.`);
        }
    };

    const next = () => {
        if (index + 1 < SIZE_LEVELS.length) {
            setIndex(index + 1);
            setItems(shuffle(SIZE_LEVELS[index + 1].sizes));
            setWrong([]);
            setSolved(false);
            return;
        }
        setFinished(true);
        sfx.finish();
        speakText('Well done! You know big and small!');
        onFinish?.(results.filter(Boolean).length, SIZE_LEVELS.length);
    };

    const replay = () => {
        setIndex(0);
        setItems(shuffle(SIZE_LEVELS[0].sizes));
        setResults([]);
        setWrong([]);
        setSolved(false);
        setFinished(false);
    };

    const dots = <Dots levels={SIZE_LEVELS} results={results} index={index} finished={finished} />;

    if (finished) {
        return (
            <div className="bbx">
                <Final correct={results.filter(Boolean).length} total={SIZE_LEVELS.length} title="Size superstar!" onReplay={replay}>{dots}</Final>
                <style>{styles}</style>
            </div>
        );
    }

    return (
        <div className="bbx">
            <LevelBar text={`Level ${level.id} of ${SIZE_LEVELS.length}`} difficulty={level.difficulty}>{dots}</LevelBar>
            <div className="bbx-sizes">
                {items.map((size) => {
                    const isRight = solved && size === target;
                    const isWrong = wrong.includes(size);
                    return (
                        <button
                            type="button"
                            key={size}
                            className={`bbx-slot ${isRight ? 'right' : ''} ${isWrong ? 'wrong' : ''} ${solved && !isRight ? 'dim' : ''}`}
                            onClick={() => choose(size)}
                            disabled={solved || isWrong}
                            style={{ fontSize: size }}
                        >
                            {level.emoji}
                        </button>
                    );
                })}
            </div>
            <div className="bbx-bottom">
                {solved
                    ? <button type="button" className="bbx-btn" onClick={next}>{index + 1 < SIZE_LEVELS.length ? 'Next ➜' : 'Finish 🏁'}</button>
                    : <div className="bbx-tip">{level.ask === 'big' ? 'Big like an elephant! 🐘' : 'Small like a mouse! 🐭'}</div>}
            </div>
            <style>{styles}</style>
        </div>
    );
};

// ─── What's Missing? ────────────────────────────────────────────────────────

// Preschool level: 2-3 pictures, a long look, and only 2 answers to pick
// from (3 in the last levels).
const MISSING_LEVELS = [
    { id: 1, difficulty: 'Easy', items: ['🍎', '🐶'], lookMs: 5000, choices: 2 },
    { id: 2, difficulty: 'Easy', items: ['🚗', '🐱'], lookMs: 5000, choices: 2 },
    { id: 3, difficulty: 'Easy', items: ['⚽', '🍌', '🐰'], lookMs: 6000, choices: 2 },
    { id: 4, difficulty: 'Easy', items: ['🐸', '🎈', '🍓'], lookMs: 6000, choices: 2 },
    { id: 5, difficulty: 'Medium', items: ['🐻', '🍉', '🌙'], lookMs: 6000, choices: 3 },
    { id: 6, difficulty: 'Medium', items: ['🦋', '🍪', '🚂'], lookMs: 6000, choices: 3 },
];
const DISTRACTORS = ['🐮', '🍋', '🔔', '🚌', '🐝', '🍩', '🎀', '🐔', '🥕', '🪁', '🐠', '🍦'];
const LOOK = 'Look carefully and remember!';
const ASK = 'Which one is missing?';

const buildRound = (level) => {
    const missingIdx = Math.floor(Math.random() * level.items.length);
    const missing = level.items[missingIdx];
    const others = shuffle(DISTRACTORS.filter((d) => !level.items.includes(d))).slice(0, level.choices - 1);
    return { missingIdx, missing, choices: shuffle([missing, ...others]) };
};

export const WhatsMissing = ({ onFinish }) => {
    const [index, setIndex] = useState(0);
    const [round, setRound] = useState(() => buildRound(MISSING_LEVELS[0]));
    const [phase, setPhase] = useState('look'); // look | guess | solved
    const [countdown, setCountdown] = useState(0);
    const [results, setResults] = useState([]);
    const [wrong, setWrong] = useState([]);
    const [finished, setFinished] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const timer = useRef(null);

    const level = MISSING_LEVELS[index];

    // Look phase: count down, then hide one item and ask.
    useEffect(() => {
        if (finished) return undefined;
        setPhase('look');
        let left = Math.ceil(level.lookMs / 1000);
        setCountdown(left);
        speakText(LOOK);
        timer.current = setInterval(() => {
            left -= 1;
            setCountdown(left);
            if (left <= 0) {
                clearInterval(timer.current);
                setPhase('guess');
                speakText(ASK);
            }
        }, 1000);
        return () => clearInterval(timer.current);
    }, [index, attempt, finished, level.lookMs]);

    useEffect(() => {
        setReplay(finished ? 'Great memory! Tap play again.' : phase === 'look' ? LOOK : ASK);
        return () => setReplay(null);
    }, [finished, phase]);
    useEffect(() => {
        setHud({ score: results.filter(Boolean).length, total: MISSING_LEVELS.length, question: phase === 'look' ? `👀 ${LOOK}` : `🤔 ${ASK}` });
        return () => setHud(null);
    }, [results, phase]);

    const choose = (emoji) => {
        if (phase !== 'guess' || wrong.includes(emoji)) return;
        if (emoji === round.missing) {
            setPhase('solved');
            setResults((r) => [...r, wrong.length === 0]);
            sfx.correct();
            speakText('Yes! You remembered!');
        } else {
            setWrong((w) => [...w, emoji]);
            sfx.wrong();
            speakText('Not that one. Try again!');
        }
    };

    const startLevel = (i) => {
        setIndex(i);
        setRound(buildRound(MISSING_LEVELS[i]));
        setWrong([]);
        setAttempt((a) => a + 1);
    };

    const next = () => {
        if (index + 1 < MISSING_LEVELS.length) {
            startLevel(index + 1);
            return;
        }
        setFinished(true);
        sfx.finish();
        speakText('Wonderful! You have a great memory!');
        onFinish?.(results.filter(Boolean).length, MISSING_LEVELS.length);
    };

    const replay = () => {
        setResults([]);
        setFinished(false);
        startLevel(0);
    };

    const dots = <Dots levels={MISSING_LEVELS} results={results} index={index} finished={finished} />;

    if (finished) {
        return (
            <div className="bbx">
                <Final correct={results.filter(Boolean).length} total={MISSING_LEVELS.length} title="Memory master!" onReplay={replay}>{dots}</Final>
                <style>{styles}</style>
            </div>
        );
    }

    return (
        <div className="bbx">
            <LevelBar text={`Level ${level.id} of ${MISSING_LEVELS.length}`} difficulty={level.difficulty}>{dots}</LevelBar>
            <div className="bbx-row">
                {level.items.map((emoji, i) => {
                    const hidden = phase !== 'look' && i === round.missingIdx;
                    const revealed = phase === 'solved' && hidden;
                    return (
                        <div key={`${level.id}-${i}`} className={`bbx-item ${hidden ? 'hidden' : ''} ${revealed ? 'right' : ''}`}>
                            {hidden && !revealed ? '❔' : emoji}
                        </div>
                    );
                })}
            </div>
            <div className="bbx-bottom bbx-bottom-lg">
                {phase === 'look' && <div className="bbx-timer">{countdown}</div>}
                {phase === 'guess' && (
                    <div className="bbx-choices">
                        {round.choices.map((emoji) => (
                            <button
                                type="button"
                                key={emoji}
                                className={`bbx-choice ${wrong.includes(emoji) ? 'wrong' : ''}`}
                                onClick={() => choose(emoji)}
                                disabled={wrong.includes(emoji)}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                )}
                {phase === 'solved' && (
                    <button type="button" className="bbx-btn" onClick={next}>{index + 1 < MISSING_LEVELS.length ? 'Next ➜' : 'Finish 🏁'}</button>
                )}
            </div>
            <style>{styles}</style>
        </div>
    );
};

// ─── Preschool picture quizzes (Animal Homes, Goes Together, …) ─────────────
// One component for every quiz in preschoolQuizzes.js: prompt read aloud,
// big picture answers with a word under them, wrong tap = retry.

export const PictureQuiz = ({ quiz, onFinish }) => {
    const rounds = quiz.rounds;
    const [index, setIndex] = useState(0);
    const [choices, setChoices] = useState(() => shuffle(rounds[0].choices));
    const [results, setResults] = useState([]);
    const [wrong, setWrong] = useState([]);
    const [solved, setSolved] = useState(false);
    const [finished, setFinished] = useState(false);

    const round = rounds[index];

    useEffect(() => {
        const t = setTimeout(() => speakText(round.prompt), 400);
        return () => clearTimeout(t);
    }, [round]);
    useEffect(() => {
        setReplay(finished ? 'Great job! Tap play again.' : solved ? round.explain : round.prompt);
        return () => setReplay(null);
    }, [finished, solved, round]);
    useEffect(() => {
        setHud({ score: results.filter(Boolean).length, total: rounds.length, question: round.prompt });
        return () => setHud(null);
    }, [results, round, rounds.length]);

    const choose = (choice) => {
        if (solved || wrong.includes(choice.id)) return;
        if (choice.id === round.answer) {
            setSolved(true);
            setResults((r) => [...r, wrong.length === 0]);
            sfx.correct();
            speakText(`Yes! ${round.explain}`);
        } else {
            setWrong((w) => [...w, choice.id]);
            sfx.wrong();
            speakText(`That is ${choice.label}. Try again!`);
        }
    };

    const startRound = (i) => {
        setIndex(i);
        setChoices(shuffle(rounds[i].choices));
        setWrong([]);
        setSolved(false);
    };

    const next = () => {
        if (index + 1 < rounds.length) {
            startRound(index + 1);
            return;
        }
        setFinished(true);
        sfx.finish();
        speakText('Hooray! You did it!');
        onFinish?.(results.filter(Boolean).length, rounds.length);
    };

    const replay = () => {
        setResults([]);
        setFinished(false);
        startRound(0);
    };

    const dots = <Dots levels={rounds} results={results} index={index} finished={finished} />;

    if (finished) {
        return (
            <div className="bbx">
                <Final correct={results.filter(Boolean).length} total={rounds.length} title={quiz.perfect} onReplay={replay}>{dots}</Final>
                <style>{styles}</style>
            </div>
        );
    }

    return (
        <div className="bbx">
            <LevelBar text={`Level ${round.id} of ${rounds.length}`} difficulty={round.difficulty}>{dots}</LevelBar>
            {round.show && (
                <button type="button" className="bbx-show" onClick={() => speakText(round.prompt)} aria-label="Say it again">
                    {round.show}
                </button>
            )}
            <div className="bbx-choices">
                {choices.map((c) => {
                    const isRight = solved && c.id === round.answer;
                    const isWrong = wrong.includes(c.id);
                    return (
                        <button
                            type="button"
                            key={c.id}
                            className={`bbx-pchoice ${isRight ? 'right' : ''} ${isWrong ? 'wrong' : ''} ${solved && !isRight ? 'dim' : ''}`}
                            onClick={() => choose(c)}
                            disabled={solved || isWrong}
                        >
                            <span className="bbx-pemoji">{c.emoji}</span>
                            <span className="bbx-plabel">{c.label}</span>
                        </button>
                    );
                })}
            </div>
            <div className="bbx-bottom">
                {solved && (
                    <>
                        <div className="bbx-done">🎉 {round.explain}</div>
                        <button type="button" className="bbx-btn" onClick={next}>{index + 1 < rounds.length ? 'Next ➜' : 'Finish 🏁'}</button>
                    </>
                )}
            </div>
            <style>{styles}</style>
        </div>
    );
};

const styles = `
    .bbx { padding: 10px 16px 12px; font-family: 'Baloo 2', 'Fredoka One', 'Comic Sans MS', cursive; color: #1E293B; text-align: center; }
    .bbx-title { font-size: 1.6rem; font-weight: 900; margin: 10px 0 14px; }
    .bbx-dots { display: flex; flex-wrap: wrap; justify-content: center; gap: 6px; }
    .bbx-dot { width: 28px; height: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 900; color: #fff; border: 3px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,.15); }
    .bbx-dot.now { transform: scale(1.2); }
    .bbx-levelbar { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; max-width: 760px; margin: 0 auto 6px; font-weight: 800; color: #475569; }
    .bbx-leveltext { font-size: 1.05rem; }
    .bbx-chip { color: #fff; border-radius: 12px; padding: 2px 12px; font-size: .85rem; font-weight: 900; }
    .bbx-bottom { margin-top: 12px; min-height: 56px; display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 8px 16px; }
    .bbx-tip { font-size: 1.05rem; font-weight: 700; color: #64748B; }
    .bbx-done { font-size: 1.35rem; font-weight: 900; color: #166534; }
    .bbx-btn { min-height: 50px; padding: 0 30px; border: none; border-radius: 25px; background: #16A34A; color: #fff; font-size: 1.25rem; font-weight: 900; cursor: pointer; box-shadow: 0 3px 8px rgba(0,0,0,.18); font-family: inherit; }
    .bbx-btn:hover { filter: brightness(1.08); }

    .bbx-memory { display: grid; gap: 12px; justify-content: center; margin: 14px auto 0; }
    .bbx-mcard { aspect-ratio: 1; width: 100%; border-radius: 18px; border: 3px solid #7C3AED; background: #8B5CF6; font-size: clamp(1.8rem, 6vw, 3rem); cursor: pointer; transition: transform .25s, background .25s; box-shadow: 0 4px 10px rgba(0,0,0,.12); }
    .bbx-mcard:hover:not(:disabled) { transform: translateY(-3px); }
    .bbx-mcard.open { background: #fff; border-color: #E2E8F0; transform: rotateY(360deg); cursor: default; }
    .bbx-mcard.matched { background: #DCFCE7; border-color: #22C55E; }

    .bbx-sizes { display: flex; flex-wrap: wrap; justify-content: center; align-items: flex-end; gap: 14px; min-height: 150px; }
    .bbx-slot { min-width: 120px; min-height: 120px; padding: 12px; border-radius: 22px; border: 3px solid #E2E8F0; background: #fff; line-height: 1; cursor: pointer; transition: transform .2s; }
    .bbx-slot:hover:not(:disabled) { transform: scale(1.05); }
    .bbx-slot.right { border-color: #22C55E; background: #DCFCE7; }
    .bbx-slot.wrong { border-color: #FCA5A5; background: #FEF2F2; opacity: .55; }
    .bbx-slot.dim { opacity: .45; }

    .bbx-row { display: flex; flex-wrap: wrap; justify-content: center; gap: 18px; }
    /* What's Missing: room for the big answer buttons so the card doesn't jump between phases. */
    .bbx-bottom.bbx-bottom-lg { margin-top: 16px; min-height: 150px; flex-direction: column; }
    .bbx-item { width: 130px; height: 130px; border-radius: 26px; border: 4px solid #E2E8F0; background: #fff; display: flex; align-items: center; justify-content: center; font-size: 4.6rem; line-height: 1; box-shadow: 0 4px 12px rgba(0,0,0,.08); }
    .bbx-item.hidden { border-style: dashed; border-color: #F59E0B; background: #FFFBEB; }
    .bbx-item.right { border-style: solid; border-color: #22C55E; background: #DCFCE7; }
    .bbx-timer { width: 80px; height: 80px; border-radius: 50%; background: #8B5CF6; color: #fff; font-size: 2rem; font-weight: 900; display: flex; align-items: center; justify-content: center; }
    .bbx-choices { display: flex; gap: 22px; }
    .bbx-choice { width: 170px; height: 170px; border-radius: 30px; border: 4px solid #93C5FD; background: #EFF6FF; font-size: 6.2rem; line-height: 1; cursor: pointer; transition: transform .2s; box-shadow: 0 4px 12px rgba(0,0,0,.08); }
    .bbx-choice:hover:not(:disabled) { transform: scale(1.06); }
    .bbx-choice.wrong { border-color: #FCA5A5; background: #FEF2F2; opacity: .45; }

    .bbx-show { width: 180px; height: 180px; margin: 4px auto 0; display: flex; align-items: center; justify-content: center; border-radius: 50%; border: 4px solid #FFE0B2; background: #FFF8E1; font-size: 7rem; cursor: pointer; }
    .bbx-pchoice { width: 240px; padding: 22px 12px; border-radius: 28px; border: 4px solid #93C5FD; background: #EFF6FF; cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 4px; transition: transform .2s; font-family: inherit; }
    .bbx-pchoice:hover:not(:disabled) { transform: scale(1.06); }
    .bbx-pchoice.right { border-color: #22C55E; background: #DCFCE7; }
    .bbx-pchoice.wrong { border-color: #FCA5A5; background: #FEF2F2; opacity: .45; }
    .bbx-pchoice.dim { opacity: .4; }
    .bbx-pemoji { font-size: 7rem; line-height: 1.1; }
    .bbx-plabel { font-size: 1.8rem; font-weight: 900; color: #1E293B; }
    .bbx-choices { flex-wrap: wrap; justify-content: center; margin-top: 16px; }

    .bbx-final { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 12px; }
    .bbx-final h3 { font-size: 1.8rem; font-weight: 900; margin: 0; }
    .bbx-final p { font-size: 1.1rem; font-weight: 800; color: #475569; margin: 0 0 8px; }
    .bbx-stars { font-size: 2.4rem; }

    @media (max-width: 575px) {
        .bbx-title { font-size: 1.3rem; }
        .bbx-item { width: 92px; height: 92px; font-size: 3.2rem; border-radius: 20px; }
        .bbx-row { gap: 12px; }
        .bbx-choice { width: 124px; height: 124px; font-size: 4.4rem; border-radius: 22px; }
        .bbx-choices { gap: 14px; }
        .bbx-pchoice { width: 150px; padding: 14px 6px; }
        .bbx-pemoji { font-size: 4.6rem; }
        .bbx-plabel { font-size: 1.35rem; }
        .bbx-show { width: 130px; height: 130px; font-size: 5rem; }
        .bbx-leveltext { font-size: .9rem; }
    }
`;
